import { SidebarSimpleIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Segmented } from '~/components/ui/segmented';
import type { CanvasLayout } from './canvas-layout';

type NarrowView = 'chat' | 'canvas';

/**
 * The item count as a small solid primary pill, so the number reads as a count
 * in its own colour in both themes (a tinted pill all but vanished on the dark
 * card). Near-white primary-foreground on primary clears 4.5:1 in both themes.
 * It is hidden from the accessible name (the button's own aria-label says
 * "Canvas, 3 items").
 */
export function CanvasCountPill({ count }: { count: number }) {
  return (
    <span
      data-testid="canvas-count"
      aria-hidden
      className="inline-flex items-center rounded-full bg-primary px-1.5 text-[length:var(--text-2xs)] font-medium tabular-nums leading-4 text-primary-foreground"
    >
      {count}
    </span>
  );
}

/**
 * FRONT-2594 — the chat header's Canvas entry point. Wide screens: one toggle
 * button "Canvas · N". Narrow screens: a Chat | Canvas segmented control (one
 * view at a time) whose Canvas segment carries a dot when something changed
 * while the person was looking at the chat. Nothing in the `off` layout.
 *
 * The narrow switch is rendered twice by the modal, once per placement, and CSS
 * shows exactly one: `header` (from `sm` up, inside the header bar) and `row`
 * (below `sm`, its own full-width row under the header so the header keeps
 * every control it has without Canvas). `row` renders nothing for the wide
 * button layouts. The two copies use distinct test ids.
 */
export function CanvasHeaderControls({
  layout,
  count,
  unseen,
  onSetOpen,
  placement = 'header',
}: {
  layout: CanvasLayout;
  count: number;
  unseen: boolean;
  onSetOpen: (open: boolean) => void;
  placement?: 'header' | 'row';
}) {
  if (layout === 'off') return null;
  const name = `Canvas, ${count} ${count === 1 ? 'item' : 'items'}`;
  const row = placement === 'row';
  if (layout === 'closed' || layout === 'wide') {
    if (row) return null;
    const open = layout === 'wide';
    return (
      <Button
        type="button"
        size="sm"
        variant={open ? 'secondary' : 'outline'}
        aria-pressed={open}
        data-testid="canvas-toggle"
        tooltip={open ? 'Hide Canvas' : 'Show Canvas'}
        onClick={() => onSetOpen(!open)}
        aria-label={name}
        className="shrink-0 gap-1.5"
      >
        <SidebarSimpleIcon size={14} aria-hidden />
        Canvas
        <CanvasCountPill count={count} />
      </Button>
    );
  }
  const ids = row
    ? { wrap: 'canvas-mobile-toggle-row', chat: 'canvas-mobile-row-chat', canvas: 'canvas-mobile-row-canvas', dot: 'canvas-unseen-dot-row' }
    : { wrap: 'canvas-mobile-toggle', chat: 'canvas-mobile-chat', canvas: 'canvas-mobile-canvas', dot: 'canvas-unseen-dot' };
  return (
    <div
      data-testid={ids.wrap}
      data-canvas-region={row ? 'toggle-row' : undefined}
      className={
        row
          ? 'sm:hidden shrink-0 flex h-11 items-center justify-center border-b border-border px-3'
          : 'max-sm:hidden shrink-0'
      }
    >
      <Segmented<NarrowView>
        size="sm"
        aria-label="Chat or Canvas"
        value={layout === 'narrow-canvas' ? 'canvas' : 'chat'}
        onChange={(v) => onSetOpen(v === 'canvas')}
        options={[
          { value: 'chat', label: 'Chat', testId: ids.chat },
          {
            value: 'canvas',
            testId: ids.canvas,
            ariaLabel: name,
            label: (
              <span className="inline-flex items-center gap-1.5">
                Canvas
                <CanvasCountPill count={count} />
                {unseen && (
                  <span
                    data-testid={ids.dot}
                    role="img"
                    aria-label="New in Canvas"
                    className="size-1.5 rounded-full bg-primary"
                  />
                )}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}
